import { useCallback, useEffect, useRef, useState } from 'react'
import { type Store } from '@reduxjs/toolkit'
import { useStore } from 'react-redux'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'

import { useReportCrossClientViewMutation } from '../../../store/crossClient/crossClient'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { type RootState } from '../../../store/store'
import {
  addCrossClientClients,
  addCrossClientFetchedClientIds,
  addCrossClientLookedUpClientIds,
  removeCrossClientFetchedClientIds,
  selectCrossClientClients,
  selectCrossClientFetchedClientIds,
  selectCrossClientResultsExecution,
} from '../../../store/crossClient/crossClientResults'
import { executionShowsResults } from '../../../store/crossClient/utils'
import { type CrossClientStartedQuery, getStartedQuery } from '../utils'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { useLazyGetAllUsersQuery } from '../../../store/user/user'
import { selectAccountId } from '../../../store/auth/authSlice'
import { type User } from '../../../models/User'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { type CrossClientQueryResult } from '../../../models/CrossClient'

const FETCH_CHUNK_SIZE = 500

// Lookups under way by client id, per store, so a caller that needs a client another lookup is loading waits for that one.
const lookupsUnderWayByStore = new WeakMap<Store<RootState>, Map<string, Promise<void>>>()

const lookupsUnderWay = (store: Store<RootState>): Map<string, Promise<void>> => {
  const existing = lookupsUnderWayByStore.get(store)
  if (existing) {
    return existing
  }
  const created = new Map<string, Promise<void>>()
  lookupsUnderWayByStore.set(store, created)
  return created
}

export const useFetchMissingCrossClientClients = () => {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const accountId = useAppSelector(selectAccountId)
  const [triggerGetAllUsers] = useLazyGetAllUsersQuery()

  // Reads the attempted ids from the store, not a dependency: a lookup changing them would otherwise
  // give effects that call this a new function, and a failed lookup would rerun them straight away.
  // A client another lookup is loading is waited for, read again if that lookup failed, and returned too.
  return useCallback(
    async function fetchMissingClients(clientIds: string[]): Promise<Record<string, User>> {
      const underWay = lookupsUnderWay(store)
      const fetchedClientIds = selectCrossClientFetchedClientIds(store.getState())
      const missingIds = clientIds.filter((id) => !fetchedClientIds[id])
      const waitedIds = clientIds.filter((id) => fetchedClientIds[id] && underWay.has(id))
      const waits = Array.from(new Set(waitedIds.map((id) => underWay.get(id))))

      const readChunk = async (chunk: string[]) => {
        const response = await triggerGetAllUsers({ accountId, ids: chunk }).unwrap()
        if (response?.data) {
          dispatch(addCrossClientClients(response.data.filter((contact) => contact.roles.some((role) => role.name === 'Client'))))
        }
      }

      const lookUp = async () => {
        dispatch(addCrossClientFetchedClientIds(missingIds))
        try {
          const chunks = Array.from({ length: Math.ceil(missingIds.length / FETCH_CHUNK_SIZE) }, (_, index) =>
            missingIds.slice(index * FETCH_CHUNK_SIZE, (index + 1) * FETCH_CHUNK_SIZE),
          )
          await chunks.slice(1).reduce((previous, chunk) => previous.then(() => readChunk(chunk)), readChunk(chunks[0]))
        } catch (e) {
          dispatch(removeCrossClientFetchedClientIds(missingIds))
          throw e
        } finally {
          dispatch(addCrossClientLookedUpClientIds(missingIds))
        }
      }

      if (missingIds.length) {
        const lookup = lookUp()
        const settled = lookup.then(
          () => undefined,
          () => undefined,
        )
        missingIds.forEach((id) => underWay.set(id, settled))
        void settled.then(() => {
          missingIds.forEach((id) => {
            if (underWay.get(id) === settled) {
              underWay.delete(id)
            }
          })
        })
        await Promise.all([lookup, ...waits])
      } else {
        await Promise.all(waits)
      }

      const fetchedNow = selectCrossClientFetchedClientIds(store.getState())
      const unsettledIds = waitedIds.filter((id) => !fetchedNow[id] || underWay.has(id))
      if (unsettledIds.length) {
        await fetchMissingClients(unsettledIds)
      }
      const clients = selectCrossClientClients(store.getState())
      return Object.fromEntries(clientIds.filter((id) => clients[id]).map((id) => [id, clients[id]]))
    },
    [accountId, dispatch, store, triggerGetAllUsers],
  )
}

export const useExportCrossClientResults = (resolveResults: () => Promise<CrossClientQueryResult[]>) => {
  const { tracked } = useTracking()

  const execution = useAppSelector(selectCrossClientResultsExecution)

  const fetchClients = useFetchMissingCrossClientClients()

  const exportCSV = tracked(() => _exportCSV(), {
    eventName: TrackEvent.X_CLIENT_RESULT_EXPORTED,
    eventDetails: {
      executionId: execution?.id,
    },
  })

  const _exportCSV = async () => {
    const results = await resolveResults()
    if (!results.length) {
      return
    }

    const clients = await fetchClients(results.map((result) => result.clientId))

    const csvRows = [
      ['Client', 'Email', 'Phone Number'],
      ...results.map((result) => {
        const client = clients[result.clientId]
        const clientName = client ? getUserDisplayName(client) : 'Unknown'
        const clientEmail = client ? client.email : 'Unknown'
        const clientPhoneNumber = client && client.phoneNumber ? client.phoneNumber : ''

        return [clientName, clientEmail, clientPhoneNumber]
      }),
    ]

    const csvContent = csvRows.map((row) => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.href = url
    link.download = `CrossClientResults_${execution?.id}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  return { exportCSV }
}

export const useReportCrossClientResultsView = (executionId: string): void => {
  const accountId = useAppSelector(selectAccountId)
  const execution = useAppSelector(selectCrossClientResultsExecution)

  const [reportCrossClientView] = useReportCrossClientViewMutation()

  const handledExecutionRef = useRef<string | null>(null)

  useEffect(() => {
    if (!accountId || handledExecutionRef.current === executionId || execution?.id !== executionId || !executionShowsResults(execution)) {
      return
    }
    if (execution.usageReportedAt) {
      handledExecutionRef.current = executionId
      return
    }
    handledExecutionRef.current = executionId

    const reportView = async () => {
      try {
        await reportCrossClientView({ accountId, executionId }).unwrap()
      } catch {
        if (handledExecutionRef.current === executionId) {
          handledExecutionRef.current = null
        }
      }
    }

    void reportView()
  }, [accountId, executionId, execution, reportCrossClientView])
}

export interface CrossClientJustStarted extends CrossClientStartedQuery {
  executionId: string | undefined
}

// History state survives a reload, so the landing's mark is read once, for the execution it came with, and dropped at once.
export const useJustStarted = (): CrossClientJustStarted | undefined => {
  const { state } = useLocation()
  const { executionId } = useParams()
  const [justStarted] = useState(() => {
    const startedQuery = getStartedQuery(state)
    return startedQuery ? { ...startedQuery, executionId } : undefined
  })
  return justStarted
}

export const useDropJustStartedMark = (): void => {
  const { state } = useLocation()
  const [, setSearchParams] = useSearchParams()
  const markInHistory = getStartedQuery(state) !== undefined

  useEffect(() => {
    if (!markInHistory) {
      return
    }
    setSearchParams((previous) => previous, { replace: true, state: null })
  }, [markInHistory, setSearchParams])
}
